import { APP_VERSION } from "@solomon-apps/config";
import posthog from "posthog-js";

import { detectDeployEnv } from "../utils/environment";

const POSTHOG_KEY = "phc_rDiXEd1UkQdDXulfJPACXx3rbmdMChZ5IHRhb7QSRbx";
// Direct PostHog ingestion endpoint. Used on localhost; on production /
// staging we route through the Netlify reverse proxy at `/sl-relay` (see
// `netlify.toml`) so analytics traffic looks first-party and isn't dropped
// by ad-blockers. The path prefix is intentionally Solomon-specific —
// generic paths like `/ingest`, `/track`, or `/analytics` are themselves
// on common filter lists.
const POSTHOG_DIRECT_HOST = "https://us.i.posthog.com";
const POSTHOG_PROXY_HOST = "/sl-relay";
// `ui_host` always points at the real PostHog UI so deep-links (e.g. the
// "View in PostHog" buttons surfaced by the SDK) resolve correctly.
const POSTHOG_UI_HOST = "https://us.posthog.com";

let initialized = false;

/**
 * Initialize PostHog analytics on production, staging, and localhost. Other
 * hosts (e.g. ad-hoc Netlify deploy previews) are skipped so we don't pollute
 * the project with unclassified traffic. Every event is tagged with an
 * `environment` super-property so prod dashboards can filter staging/local out.
 */
export function initAnalytics(): void {
    if (initialized) {
        return;
    }
    const env = detectDeployEnv();
    if (env === null) {
        return;
    }

    // Localhost has no Netlify rewrite layer, so it must hit PostHog
    // directly; production and staging go through the reverse proxy.
    const apiHost = env === "local" ? POSTHOG_DIRECT_HOST : POSTHOG_PROXY_HOST;

    posthog.init(POSTHOG_KEY, {
        api_host: apiHost,
        ui_host: POSTHOG_UI_HOST,
        capture_pageview: false,
        capture_pageleave: true,
        autocapture: true,
        person_profiles: "identified_only",
        disable_session_recording: true,
        cross_subdomain_cookie: false,
        // Verbose logs are useful when verifying events fire from localhost /
        // staging; harmless on prod and gated by the deploy env above anyway.
        debug: env !== "production"
    });

    posthog.register(analyticsContext());

    initialized = true;
}

function getAppName(): string {
    // esbuild substitutes `process.env.APP` at build time per
    // `*.esbuild.config.json` `define` blocks, but TypeScript doesn't model
    // that, so guard with typeof.
    if (typeof process !== "undefined" && process.env?.APP) {
        return process.env.APP;
    }
    return "unknown";
}

function detectDeviceType(): "mobile" | "desktop" {
    if (typeof window === "undefined" || !window.matchMedia) {
        return "desktop";
    }
    // Tailwind `md` breakpoint — matches `useIsMobile()` convention.
    return window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop";
}

/**
 * Privacy-preserving id for analytics: SHA-256 of the input, truncated to
 * 16 hex chars (64 bits). Used to identify a wallet without shipping the
 * raw public key, and to hash invite codes before sending to PostHog.
 *
 * Returns an empty string when Web Crypto isn't available (SSR, very old
 * browsers); callers should treat empty as "skip the identify call".
 */
export async function hashForAnalytics(input: string): Promise<string> {
    if (typeof crypto === "undefined" || !crypto.subtle) {
        return "";
    }
    const data = new TextEncoder().encode(input);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hashBuffer))
        .slice(0, 8)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

/**
 * Capture a pageview event. No-op if PostHog is not initialized.
 */
export function capturePageview(url: string): void {
    if (!initialized) {
        return;
    }
    posthog.capture("$pageview", {
        $current_url: url
    });
}

/** Stable app properties must be restored after a wallet identity reset. */
export function analyticsContext(): {
    environment: ReturnType<typeof detectDeployEnv>;
    app: string;
    app_version: string;
    device_type: "mobile" | "desktop";
} {
    return {
        environment: detectDeployEnv(),
        app: getAppName(),
        app_version: APP_VERSION,
        device_type: detectDeviceType()
    };
}

export function isAnalyticsEnabled(): boolean {
    if (!initialized || getAppName() !== "main" || typeof window === "undefined") return false;
    const query = new URLSearchParams(window.location.search);
    return (
        !/^\/(showcase|wip)(\/|$)/.test(window.location.pathname) &&
        !query.has("state") &&
        !query.has("simulate") &&
        !("__SOLOMON_E2E__" in window)
    );
}
